<script setup lang="ts">
import { defineProps, watch, ref, defineEmits } from "vue";
import DayParamsComponent from "@/views/Calculators/PersonalDates/components/dayReport/DayParamsComponent.vue";
import DayReportControlsComponent from "@/views/Calculators/PersonalDates/components/dayReport/DayReportControlsComponent.vue";
import {
  FavorableDateSelectionRequest,
  FavorableDayReport,
  MonthDayReport,
} from "@/types/calculators";
import { fetchDayReport } from "@/api/favorableDates";
import LoaderIcon from "@/components/icons/LoaderIcon.vue";
import DayDoingsComponent from "@/views/Calculators/PersonalDates/components/dayReport/DayDoingsComponent.vue";
import DailyHealthTaboosComponent from "@/views/Calculators/PersonalDates/components/dayReport/DailyHealthTaboosComponent.vue";
import HourlyReportTableComponent from "@/views/Calculators/PersonalDates/components/dayReport/HourlyReportTableComponent.vue";

const props = defineProps<{
  lastRequest: FavorableDateSelectionRequest;
  selectedCard: MonthDayReport;
}>();
defineEmits(["backToCalendar", "nextDay", "prevDay"]);

const dayDetailsInfo = ref<FavorableDayReport | null>(null);
const waitApiCall = ref<boolean>(false);

watch(
  () => props.selectedCard,
  async (newCard) => {
    if (
      dayDetailsInfo.value === null ||
      dayDetailsInfo.value.meta.target_date !== newCard.date
    ) {
      waitApiCall.value = true;
      dayDetailsInfo.value = await fetchDayReport(
        props.lastRequest,
        newCard.date
      );
      waitApiCall.value = false;
    }
  },
  { deep: true, immediate: true }
);
</script>

<template>
  <DayReportControlsComponent
    @backToCalendar="$emit('backToCalendar')"
    @nextDay="$emit('nextDay')"
    @prevDay="$emit('prevDay')"
  />
  <LoaderIcon
    spinnerColor="brown"
    class="mx-auto"
    v-if="waitApiCall || !dayDetailsInfo"
  />
  <template v-else>
    <DayParamsComponent
      :selectedCard="selectedCard"
      :dayDetailsInfo="dayDetailsInfo"
      :latitude="lastRequest.latitude"
      :longitude="lastRequest.longitude"
      :timezone="lastRequest.timezone"
    />
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-5 sm:gap-6">
      <DayDoingsComponent
        :dayDetailsInfo="dayDetailsInfo"
        :activityScope="lastRequest.activity_scope"
      />
      <DailyHealthTaboosComponent
        :bodyAndStem="dayDetailsInfo.day_report.general.shen_sha.body_and_stem"
      />
    </div>
    <HourlyReportTableComponent
      :timeOffset="
        dayDetailsInfo.day_report.general.astronomical_watches
          .solar_time_offset_min
      "
      :hours="dayDetailsInfo.hours_report"
    />
  </template>
</template>
